/**
 * The small markdown the setup instructions use, parsed to a tree.
 *
 * Deliberately not a markdown library, and deliberately **not a function that
 * returns HTML**. These instructions are the largest block of free text on an
 * unauthenticated page — a floor plan's notes and piece captions reach it too,
 * and `docs/public-schedule.md` says so — and whoever types them may be a
 * client. A general renderer passes raw HTML through by default, so the usual
 * shape is a renderer plus a sanitiser, both of which have to stay correctly
 * configured forever.
 *
 * This returns blocks and inline spans instead. The web maps them to React
 * elements, so there is no HTML string anywhere in the path and nothing to feed
 * to `dangerouslySetInnerHTML` — a `<script>` typed into the box is a paragraph
 * that says `<script>`, because text is the only thing this can produce.
 *
 * It understands exactly what the toolbar writes:
 *
 * - `**bold**`
 * - `- ` bulleted lists
 * - `1. ` numbered lists
 * - blank-line-separated paragraphs
 *
 * Everything else is text. See `docs/public-schedule.md`.
 */

/** A run of text, optionally bold. Nothing else is expressible. */
export interface InlineSpan {
  text: string
  bold: boolean
}

/** One block of the instructions. */
export type SetupBlock =
  | { kind: 'paragraph'; spans: InlineSpan[] }
  | { kind: 'bullets'; items: InlineSpan[][] }
  | { kind: 'numbers'; items: InlineSpan[][] }

const BULLET = /^\s*[-*]\s+(.*)$/
const NUMBER = /^\s*\d+[.)]\s+(.*)$/

/**
 * Split one line into bold and plain runs.
 *
 * Unpaired `**` is left as text rather than opening formatting that never
 * closes: somebody typing about a 2**2 grid gets what they typed.
 */
export function parseInline(line: string): InlineSpan[] {
  const spans: InlineSpan[] = []
  let rest = line
  while (rest.length > 0) {
    const open = rest.indexOf('**')
    if (open === -1) break
    const close = rest.indexOf('**', open + 2)
    if (close === -1) break
    if (open > 0) spans.push({ text: rest.slice(0, open), bold: false })
    const inner = rest.slice(open + 2, close)
    // `****` is not bold-of-nothing; it is four characters somebody typed.
    if (inner.length === 0) spans.push({ text: '****', bold: false })
    else spans.push({ text: inner, bold: true })
    rest = rest.slice(close + 2)
  }
  if (rest.length > 0) spans.push({ text: rest, bold: false })
  return spans.length > 0 ? spans : [{ text: '', bold: false }]
}

/**
 * Parse instructions into blocks.
 *
 * Consecutive list lines of the same kind gather into one list; a blank line, or
 * a line of the other kind, ends it. Consecutive plain lines join into one
 * paragraph, so a wrapped sentence reads as a sentence.
 */
export function parseSetupMarkdown(source: string): SetupBlock[] {
  const blocks: SetupBlock[] = []
  let paragraph: string[] = []
  let list: { kind: 'bullets' | 'numbers'; items: InlineSpan[][] } | null = null

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return
    blocks.push({ kind: 'paragraph', spans: parseInline(paragraph.join(' ')) })
    paragraph = []
  }
  const flushList = (): void => {
    if (list === null) return
    blocks.push(list.kind === 'bullets' ? { kind: 'bullets', items: list.items } : { kind: 'numbers', items: list.items })
    list = null
  }

  for (const raw of source.replace(/\r\n?/g, '\n').split('\n')) {
    const bullet = BULLET.exec(raw)
    const numbered = bullet ? null : NUMBER.exec(raw)
    const kind = bullet ? ('bullets' as const) : numbered ? ('numbers' as const) : null

    if (kind !== null) {
      flushParagraph()
      if (list !== null && list.kind !== kind) flushList()
      const item = parseInline((bullet?.[1] ?? numbered?.[1]) ?? '')
      if (list === null) list = { kind, items: [item] }
      else list.items.push(item)
      continue
    }

    flushList()
    if (raw.trim() === '') flushParagraph()
    else paragraph.push(raw.trim())
  }
  flushList()
  flushParagraph()
  return blocks
}

/** Whether anything would render — an all-whitespace note is not instructions. */
export function hasSetupContent(source: string | null): boolean {
  return source !== null && parseSetupMarkdown(source).length > 0
}
